import {
  ACCESSORIAL_CATALOG,
  FREIGHT_CLASSES,
  classForDensity,
  densityHundredths,
  densityPcf,
  lineCubicFeet,
  type NormalizedAccessorialCode,
} from "@flexfreight/core";
import {
  AccessorialTiles,
  AddressCard,
  BookingAddressFields,
  bookingAddressErrors,
  AddressFields,
  Button,
  Combobox,
  DatePicker,
  DensityStrip,
  Dialog,
  FreightLine,
  NO_RATE_FILTERS,
  PickupDateRow,
  QuotePlusIcon,
  QuoteStepHead,
  QuoteSteps,
  RateFilterBar,
  RatePickCard,
  RateRow,
  RateStreamProgress,
  ShipmentSoFar,
  SortTabs,
  StatusPill,
  TextField,
  Toggle,
  isBusinessDay,
  isoDate,
  notify,
  type AccessorialTile,
  type AddressValue,
  type CarrierChoice,
  type ComboOption,
  type FreightLineValue,
  type LineDensity,
  type QuoteStepState,
  type RateFilters,
  type SelectOption,
} from "@flexfreight/ui";
import "@flexfreight/ui/carrier.css";
import "@flexfreight/ui/quote.css";
import "@flexfreight/ui/address.css";
import { useMutation } from "@tanstack/react-query";
import { useConsoleQuoteStream } from "./quoteStream";
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { addressSource, lookupPostal } from "../../api/geo";
import { useConsoleMe } from "../../auth/context";
import { QuoteShare } from "./QuoteShare";

type Money = { cents: number; currency: "USD" };
export type ConsoleQuoteOption = {
  id: string;
  carrier: string;
  carrierName: string;
  /** EagleLoad rates: our code for the carrier that runs the freight, when it is one we know (for its logo). */
  operatedBy?: string | null;
  /** The carrier's official logo served by the API (`/v1/carrier-logos/...`); older payloads omit it. */
  logoUrl?: string | null;
  serviceName: string;
  transitDays: number | null;
  estimatedDelivery?: string | null;
  guaranteed: boolean;
  price: Money;
  internal?: { cost: Money; margin: Money; markupRationale: string | null };
  override?: { reason: string; at: string; originalCents: number | null } | null;
};
export type ConsoleQuoteResult = {
  org: { id: string; name: string; prospect: boolean };
  quote: { id: string; status: string; options: ConsoleQuoteOption[]; errors: { carrier: string; message: string }[]; expiresAt: string | null };
  carriers: { carrier: string; mode: string }[];
  showsCost: boolean;
};

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const pct = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1 });
const count = new Intl.NumberFormat("en-US");
const money = (m: Money) => usd.format(m.cents / 100);
const dayFmt = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
/** "2026-10-22" → "Thu, Oct 22" (anchored at UTC noon so the calendar day never shifts). */
const shortDay = (iso: string | null | undefined) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? dayFmt.format(new Date(`${iso.slice(0, 10)}T12:00:00Z`)) : null);
const fullDayFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

const LOCATION_TYPES: SelectOption<LocationType>[] = [
  { value: "business_dock", label: "Business with dock" },
  { value: "business_no_dock", label: "Business, no dock" },
  { value: "residential", label: "Residential" },
  { value: "limited_access", label: "Limited access" },
  { value: "construction", label: "Construction site" },
  { value: "trade_show", label: "Trade show" },
];
type LocationType = "business_dock" | "business_no_dock" | "residential" | "limited_access" | "construction" | "trade_show";

const PACKAGING: SelectOption<Packaging>[] = [
  { value: "pallet", label: "Pallet" },
  { value: "skid", label: "Skid" },
  { value: "crate", label: "Crate" },
  { value: "box", label: "Box" },
  { value: "drum", label: "Drum" },
  { value: "other", label: "Other" },
];
type Packaging = "pallet" | "skid" | "crate" | "box" | "drum" | "other";
type FreightClassPick = "" | (typeof FREIGHT_CLASSES)[number];

/** The extra services staff can add on the phone, as the portal's tiles (labels from the core catalog). */
const EXTRAS: ReadonlyArray<{ title: string; picks: AccessorialTile[] }> = (
  [
    { title: "At pickup", stop: "pickup", codes: [["liftgate", "No dock at this stop"]] },
    { title: "At delivery", stop: "delivery", codes: [["liftgate", "No dock at this stop"], ["inside", "Past the front door"], ["appointment", "Set time window"], ["notify", "Call before arrival"]] },
  ] as const
).map((g) => ({
  title: g.title,
  picks: g.codes.map(([code, hint]) => ({ code, stop: g.stop, label: ACCESSORIAL_CATALOG[code as NormalizedAccessorialCode]?.label ?? code, hint })),
}));

type Stop = AddressValue<"US", LocationType>;
type Unit = FreightLineValue<Packaging, FreightClassPick> & { stackable: boolean };

const blankStop = (): Stop => ({ companyName: "", line1: "", city: "", region: "", postalCode: "", country: "US", type: "business_dock" });
const blankUnit = (): Unit => ({ packaging: "pallet", quantity: "1", weightLb: "", lengthIn: "48", widthIn: "40", heightIn: "", freightClass: "", description: "", stackable: false });

function nextBusinessDay(from: Date): string {
  const d = new Date(from);
  do d.setDate(d.getDate() + 1);
  while (!isBusinessDay(d));
  return isoDate(d);
}

const n = (v: string) => Number(v.replace(/,/g, ""));
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const place = (s: Stop) => (s.city.trim() ? `${s.city.trim()}${s.region.trim() ? `, ${s.region.trim().toUpperCase()}` : ""}` : s.postalCode.trim());

function lineDensity(u: Unit): LineDensity | null {
  const q = n(u.quantity), w = n(u.weightLb);
  const dims = { lengthIn: n(u.lengthIn), widthIn: n(u.widthIn), heightIn: n(u.heightIn) };
  if (!Number.isInteger(q) || q < 1 || !(w > 0) || !(dims.lengthIn > 0 && dims.widthIn > 0 && dims.heightIn > 0)) return null;
  const cubicFt = lineCubicFeet({ dims, quantity: q });
  const pcf = densityPcf(w, cubicFt);
  return { cubicFt: Math.round(cubicFt * 100) / 100, pcf: densityHundredths(pcf), suggestedClass: classForDensity(pcf) };
}

type SortKey = "price" | "transit" | "margin";
const byPrice = (a: ConsoleQuoteOption, b: ConsoleQuoteOption) => a.price.cents - b.price.cents;
const byTransit = (a: ConsoleQuoteOption, b: ConsoleQuoteOption) => (a.transitDays ?? 99) - (b.transitDays ?? 99) || byPrice(a, b);
const byMargin = (a: ConsoleQuoteOption, b: ConsoleQuoteOption) => (b.internal?.margin.cents ?? 0) - (a.internal?.margin.cents ?? 0) || byPrice(a, b);
const SORTS: Record<SortKey, (a: ConsoleQuoteOption, b: ConsoleQuoteOption) => number> = { price: byPrice, transit: byTransit, margin: byMargin };
/** Aggregator rates (EagleLoad, WWEX) share one carrier code: filter them by the carrier that actually runs them. */
const AGGREGATORS: ReadonlySet<string> = new Set(["eagleload", "wwex"]);
const AGGREGATOR_LABEL: Record<string, string> = { eagleload: "EagleLoad", wwex: "WWEX" };
const carrierKey = (o: ConsoleQuoteOption) => (AGGREGATORS.has(o.carrier) ? `${o.carrier}:${o.carrierName}` : o.carrier);
const transitText = (o: ConsoleQuoteOption) => (o.transitDays === null ? "Transit n/a" : `${o.transitDays} business day${o.transitDays === 1 ? "" : "s"}`);
const carrierRef = (o: ConsoleQuoteOption) => ({ code: o.carrier, name: o.carrierName, operatedBy: o.operatedBy ?? null, logoUrl: o.logoUrl ?? null, fallback: "monogram" as const });

/** Sales > Quoting center: quote for a customer or a prospect on the phone, with the customer's real markup. */
export function QuotingCenter() {
  const me = useConsoleMe();
  const uid = useId();
  const [params] = useSearchParams();
  // Linked from a customer page: /quotes/new?org=<id>&name=<name>. The server still checks the org on submit.
  const [target, setTarget] = useState<{ kind: "customer"; id: string; name: string } | { kind: "prospect" } | null>(() => {
    const id = params.get("org");
    const name = params.get("name");
    return id && name ? { kind: "customer", id, name } : null;
  });
  const [prospect, setProspect] = useState({ company: "", name: "", email: "", phone: "" });
  const [origin, setOrigin] = useState<Stop>(blankStop);
  const [destination, setDestination] = useState<Stop>(blankStop);
  const [pickupDate, setPickupDate] = useState<string>(() => nextBusinessDay(new Date()));
  const [units, setUnits] = useState<Unit[]>([blankUnit()]);
  const [extras, setExtras] = useState<string[]>([]);
  const [result, setResult] = useState<ConsoleQuoteResult | null>(null);
  const [view, setView] = useState<"form" | "rates">("form");
  const [sort, setSort] = useState<SortKey>("price");
  const [filters, setFilters] = useState<RateFilters>(NO_RATE_FILTERS);
  const [overriding, setOverriding] = useState<ConsoleQuoteOption | null>(null);
  const [booking, setBooking] = useState<ConsoleQuoteOption | null>(null);
  const [booked, setBooked] = useState<{ id: string; bolNumber: string | null; carrier: string } | null>(null);
  const ratesTitle = useRef<HTMLHeadingElement>(null);
  const canOverride = me.capabilities.includes("quotes:override");
  const canBook = me.capabilities.includes("quotes:book");
  const seesCost = me.capabilities.includes("pricing:cost_read");

  const searchCustomers = async (q: string): Promise<ComboOption[]> => {
    const r = await api<{ results: { id: string; name: string; prospect: boolean }[] }>(`/v1/console/customers/search?q=${encodeURIComponent(q)}`);
    return r.results.map((c) => ({ value: c.id, label: c.name, ...(c.prospect ? { detail: "Prospect" } : {}) }));
  };

  const stopReady = (s: Stop) => /^\d{5}(-\d{4})?$/.test(s.postalCode.trim()) && s.city.trim() !== "" && /^[A-Za-z]{2}$/.test(s.region.trim());
  const unitsReady = units.every((u) => n(u.quantity) > 0 && n(u.weightLb) > 0 && n(u.lengthIn) > 0 && n(u.widthIn) > 0 && n(u.heightIn) > 0 && u.description.trim() !== "");
  const targetReady = target?.kind === "customer" || (target?.kind === "prospect" && prospect.company.trim() !== "");
  const ready = targetReady && stopReady(origin) && stopReady(destination) && unitsReady && pickupDate !== "";

  // ZIP-first: the street and company are optional to rate; booking asks for the full address.
  const toLocation = (s: Stop) => ({
    type: s.type,
    ...(s.companyName.trim() ? { companyName: s.companyName.trim() } : {}),
    address: { line1: s.line1.trim(), city: s.city.trim(), region: s.region.trim().toUpperCase(), postalCode: s.postalCode.trim(), country: "US" },
  });

  // The quote body: who it's for plus the shipment (shared by the stream and the JSON fallback).
  const quoteBody = () => ({
    target:
      target?.kind === "customer"
        ? { orgId: target.id }
        : {
            prospect: Object.fromEntries(
              Object.entries({ company: prospect.company, name: prospect.name, email: prospect.email, phone: prospect.phone }).filter(([, v]) => v.trim() !== ""),
            ),
          },
    request: {
      origin: toLocation(origin),
      destination: toLocation(destination),
      pickupDate,
      units: units.map((u) => ({
        packaging: u.packaging,
        quantity: n(u.quantity),
        weightLb: n(u.weightLb),
        dims: { lengthIn: n(u.lengthIn), widthIn: n(u.widthIn), heightIn: n(u.heightIn) },
        ...(u.freightClass ? { freightClass: u.freightClass } : {}),
        description: u.description.trim(),
        stackable: u.stackable,
      })),
      accessorials: extras.map((k) => {
        const [code, stop] = k.split(":");
        return { code, stop };
      }),
    },
  });
  // Rates stream in as carriers answer (POST /v1/console/quotes/stream); the JSON route is the fallback. While the
  // stream runs the rates view shows the rows so far; the saved quote (with Book / Override) replaces them on done.
  const stream = useConsoleQuoteStream({
    onSuccess: (r) => {
      setResult(r);
      setBooked(null);
      setFilters(NO_RATE_FILTERS);
      setView("rates");
      if (r.org.prospect && target?.kind === "prospect") setTarget({ kind: "customer", id: r.org.id, name: r.org.name });
      if (r.quote.options.length === 0) notify.warning("No rates came back", "Check the ZIP codes and freight, or try another pickup date.");
    },
    onError: (e) => {
      notify.error("Couldn't get rates", message(e));
      if (!result) setView("form");
    },
  });
  const rate = {
    isPending: stream.isPending,
    mutate: () => {
      setFilters(NO_RATE_FILTERS);
      setView("rates");
      stream.mutate(quoteBody());
    },
  };
  const live = stream.isPending ? stream.live : null;
  // While rating, the rates view shows the rows that have landed so far under the carrier progress line. There is no
  // quote id yet, so nothing can be booked or overridden until `done` replaces this with the saved quote.
  const streaming = stream.isPending;
  const targetName = target?.kind === "customer" ? target.name : target?.kind === "prospect" ? prospect.company.trim() || "a new caller" : "";
  const shownResult: ConsoleQuoteResult | null = streaming
    ? {
        org: { id: target?.kind === "customer" ? target.id : "", name: targetName, prospect: target?.kind === "prospect" },
        quote: { id: "", status: "pending", options: live?.options ?? [], errors: [], expiresAt: null },
        carriers: result?.carriers ?? [],
        showsCost: result?.showsCost ?? seesCost,
      }
    : result;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (ready) rate.mutate();
  }

  // The rates replace the form in place: land keyboard and screen-reader users on the rates title.
  const resultId = result?.quote.id;
  useEffect(() => {
    if (view === "rates") ratesTitle.current?.focus();
  }, [view, resultId]);

  const options = useMemo(() => shownResult?.quote.options ?? [], [shownResult]);
  const choices = useMemo<CarrierChoice[]>(() => {
    const by = new Map<string, CarrierChoice>();
    for (const o of options) {
      const k = carrierKey(o);
      const c = by.get(k);
      if (c) c.count += 1;
      else by.set(k, { code: k, name: o.carrierName, count: 1 });
    }
    return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [options]);
  const shown = useMemo(() => options.filter((o) => (!filters.guaranteedOnly || o.guaranteed) && !filters.hidden.includes(carrierKey(o))), [options, filters]);
  const sorted = useMemo(() => [...shown].sort(SORTS[sort]), [shown, sort]);
  const cheapestId = useMemo(() => [...shown].sort(byPrice)[0]?.id, [shown]);
  const fastestId = useMemo(() => [...shown].sort(byTransit)[0]?.id, [shown]);
  const marginId = useMemo(() => (shownResult?.showsCost ? [...shown].sort(byMargin)[0]?.id : undefined), [shown, shownResult]);
  const picks = useMemo(() => {
    const out: { option: ConsoleQuoteOption; tags: string[] }[] = [];
    const add = (id: string | undefined, tag: string) => {
      const o = id ? shown.find((x) => x.id === id) : undefined;
      if (!o) return;
      const hit = out.find((p) => p.option.id === o.id);
      if (hit) hit.tags.push(tag);
      else out.push({ option: o, tags: [tag] });
    };
    add(cheapestId, "Best price");
    add(fastestId, "Fastest");
    add(marginId, "Best margin");
    return out;
  }, [shown, cheapestId, fastestId, marginId]);

  const mockCarriers = (result?.carriers ?? []).filter((c) => c.mode !== "live");
  const allMock = result !== null && mockCarriers.length === result.carriers.length;

  const setUnit = (i: number, patch: Partial<Unit>) => setUnits((us) => us.map((u, j) => (j === i ? { ...u, ...patch } : u)));
  const weight = units.reduce((s, u) => s + (n(u.weightLb) || 0), 0);
  const pieces = units.reduce((s, u) => s + (n(u.quantity) || 0), 0);
  const lane = place(origin) && place(destination) ? `${place(origin)} to ${place(destination)}` : "";

  const stepReady = [targetReady, stopReady(origin) && pickupDate !== "", stopReady(destination), unitsReady];
  const firstOpen = stepReady.findIndex((r) => !r);
  const steps: { label: string; state: QuoteStepState }[] = [
    ...["Customer", "Pickup", "Delivery", "Freight"].map((label, i) => ({ label, state: (stepReady[i] ? "done" : i === firstOpen ? "current" : "todo") as QuoteStepState })),
    { label: "Services", state: firstOpen === -1 ? (extras.length ? "done" : "current") : "todo" },
  ];
  const readyCount = stepReady.filter(Boolean).length;

  const pickNote = (o: ConsoleQuoteOption, tags: string[]) =>
    `${tags.includes("Best price") ? "Lowest sell price" : tags.includes("Fastest") ? "Earliest delivery" : "Most margin on this lane"}${o.carrier === "eagleload" ? " · via EagleLoad" : ""}`;

  // The hero price is decorative (split into dollars and cents); this line reads it, plus cost and margin for staff who see cost.
  const staffNums = (o: ConsoleQuoteOption) => (
    <>
      <p className="ff-visually-hidden">Sell price {money(o.price)}</p>
      {shownResult?.showsCost && o.internal ? (
        <dl className="ffa-qc__pickfin">
          <div><dt>Cost</dt><dd className="ffx-num">{money(o.internal.cost)}</dd></div>
          <div><dt>Margin</dt><dd className="ffx-num">{money(o.internal.margin)} <span className="ffa-qc__pct">{o.price.cents > 0 ? pct.format(o.internal.margin.cents / o.price.cents) : ""}</span></dd></div>
        </dl>
      ) : null}
    </>
  );

  return (
    <section className="ffa-page ff-quote-page ff-qw ffa-qc" aria-labelledby={`${uid}-title`}>
      <header className="ffa-head">
        <div>
          <p className="ffa-qc__eyebrow">Sales · Quoting center</p>
          <h1 id={`${uid}-title`} ref={ratesTitle} tabIndex={-1} className="ffa-page__title ffa-qc__title">{view === "rates" && shownResult ? "Choose a carrier" : "Quote a shipment"}</h1>
          <p className="ffa-head__sub">
            {view === "rates" && shownResult
              ? <>{lane || "Rates"} for <strong>{shownResult.org.name}</strong>{shownResult.org.prospect ? " (prospect)" : ""}. Each customer&apos;s own markup; the customer isn&apos;t notified.</>
              : "Quote a customer or a caller in under a minute. Rates use each customer's own markup; the customer isn't notified."}
          </p>
        </div>
        {view === "rates" && shownResult ? (
          <div className="ffa-qc__headactions">
            {result && !streaming ? <QuoteShare quoteId={result.quote.id} /> : null}
            <Button size="sm" variant="ghost" loading={rate.isPending} disabled={!ready} onClick={() => rate.mutate()}>
              Re-quote
            </Button>
            <Button size="sm" onClick={() => setView("form")}>
              Edit shipment
            </Button>
          </div>
        ) : result ? (
          <div className="ffa-qc__headactions">
            <Button size="sm" onClick={() => setView("rates")}>
              Back to rates
            </Button>
          </div>
        ) : null}
      </header>

      {view === "form" ? (
        <>
          <QuoteSteps
            label="Quote progress"
            count={<>{readyCount} of 4 required steps done · {ready ? "Ready for rates" : "Rates in about ten seconds"}</>}
            steps={steps}
          />
          <form className="ffa-qc__layout" onSubmit={submit} noValidate aria-label="Quote details">
            <div className="ffa-qc__steps">
              <section className="ffa-card ff-qw__form" aria-labelledby={`${uid}-who`}>
                <QuoteStepHead n={1} id={`${uid}-who`} title="Who it's for" hint="An existing customer (their markup applies) or a new caller, who becomes a prospect account." />
                {target?.kind === "customer" ? (
                  <div className="ffa-qc__who">
                    <span className="ffa-qc__whoicon" aria-hidden="true">{target.name.slice(0, 1).toUpperCase()}</span>
                    <span className="ffa-qc__whoname">{target.name}</span>
                    <Button size="sm" variant="ghost" onClick={() => setTarget(null)}>
                      Change
                    </Button>
                  </div>
                ) : target?.kind === "prospect" ? (
                  <div className="ffa-qc__prospect">
                    <TextField label="Company name" value={prospect.company} onChange={(e) => setProspect({ ...prospect, company: e.target.value })} autoComplete="organization" />
                    <TextField label="Contact name" optional value={prospect.name} onChange={(e) => setProspect({ ...prospect, name: e.target.value })} autoComplete="name" />
                    <TextField label="Email" optional type="email" value={prospect.email} onChange={(e) => setProspect({ ...prospect, email: e.target.value })} autoComplete="email" />
                    <TextField label="Phone" optional type="tel" value={prospect.phone} onChange={(e) => setProspect({ ...prospect, phone: e.target.value })} autoComplete="tel" />
                    <Button size="sm" variant="ghost" onClick={() => setTarget(null)}>
                      Pick an existing customer instead
                    </Button>
                  </div>
                ) : (
                  <div className="ffa-qc__pick">
                    <Combobox label="Find a customer" placeholder="Company name" load={searchCustomers} minChars={1} emptyText="No customer with that name." onSelect={(o) => setTarget({ kind: "customer", id: o.value, name: o.label })} />
                    <Button onClick={() => setTarget({ kind: "prospect" })}>New caller (prospect)</Button>
                  </div>
                )}
              </section>

              <section className="ffa-card ff-qw__form" aria-labelledby={`${uid}-from`}>
                <QuoteStepHead n={2} id={`${uid}-from`} title="Where it ships from" hint="The ZIP is enough to rate: city and state fill in. The street is needed before booking." />
                <AddressCard kind="origin" place={place(origin)} empty="Type the ZIP to rate">
                  <AddressFields prefix="origin" value={origin} onChange={setOrigin} errors={{}} locationTypes={LOCATION_TYPES} zipFirst postalLookup={lookupPostal} streetSource={addressSource} streetHint={origin.line1 ? undefined : "Needed before booking"} companyHint="Printed on the BOL" />
                </AddressCard>
                <PickupDateRow field={<DatePicker label="Pickup date" value={pickupDate} onChange={setPickupDate} variant="field" cutoffHour={null} hint={null} />} note="Carriers pick up Monday to Friday. Same-day pickups close mid-afternoon." />
              </section>

              <section className="ffa-card ff-qw__form" aria-labelledby={`${uid}-to`}>
                <QuoteStepHead n={3} id={`${uid}-to`} title="Where it's going" hint="Residential and limited-access stops change the price, so pick the right type." />
                <AddressCard kind="destination" place={place(destination)} empty="Type the ZIP to rate">
                  <AddressFields prefix="destination" value={destination} onChange={setDestination} errors={{}} locationTypes={LOCATION_TYPES} zipFirst postalLookup={lookupPostal} streetSource={addressSource} streetHint={destination.line1 ? undefined : "Needed before booking"} companyHint="Printed on the BOL" />
                </AddressCard>
              </section>

              <section className="ffa-card ff-qw__form" aria-labelledby={`${uid}-what`}>
                <QuoteStepHead n={4} id={`${uid}-what`} title="What's shipping" hint="One line per kind of handling unit. Dimensions let us check density and class before a carrier does." />
                <div className="ff-units ff-qlines">
                  {units.map((u, i) => {
                    const dens = lineDensity(u);
                    const classOptions: SelectOption<FreightClassPick>[] = [
                      { value: "", label: "Auto (density)", meta: dens ? `rates as ${dens.suggestedClass}` : undefined },
                      ...FREIGHT_CLASSES.map((c) => ({ value: c, label: `Class ${c}`, meta: dens && c === dens.suggestedClass ? "density" : undefined })),
                    ];
                    return (
                      <FreightLine
                        key={i}
                        index={i}
                        value={u}
                        onChange={(patch) => setUnit(i, patch)}
                        error={() => undefined}
                        packagingOptions={PACKAGING}
                        classOptions={classOptions}
                        onRemove={units.length > 1 ? () => setUnits((us) => us.filter((_, j) => j !== i)) : undefined}
                      >
                        <DensityStrip line={i + 1} density={dens} declaredClass={u.freightClass} />
                        <div className="ff-qline__toggles">
                          <Toggle label="Stackable" checked={u.stackable} onChange={(stackable) => setUnit(i, { stackable })} />
                        </div>
                      </FreightLine>
                    );
                  })}
                  <button type="button" className="ff-btn ff-qlines__add" onClick={() => setUnits((us) => [...us, blankUnit()])}>
                    <QuotePlusIcon /> Add another line
                  </button>
                </div>
              </section>

              <section className="ffa-card ff-qw__form" aria-labelledby={`${uid}-extra`}>
                <QuoteStepHead n={5} id={`${uid}-extra`} title="Extra services" hint="Anything the driver needs beyond a dock. Skip what doesn't apply." />
                <AccessorialTiles
                  groups={EXTRAS}
                  isChecked={(t) => extras.includes(`${t.code}:${t.stop}`)}
                  onToggle={(t) => {
                    const key = `${t.code}:${t.stop}`;
                    setExtras((xs) => (xs.includes(key) ? xs.filter((k) => k !== key) : [...xs, key]));
                  }}
                  note="Location fees (residential, limited access, no dock) come from each stop's location type."
                />
              </section>
            </div>

            <ShipmentSoFar
              panelClass="ffa-card"
              origin={place(origin)}
              destination={place(destination)}
              pickup={pickupDate ? { iso: pickupDate, text: fullDayFmt.format(new Date(`${pickupDate}T12:00:00Z`)) } : null}
              facts={[
                { label: "Handling units", value: pieces > 0 ? count.format(pieces) : "-" },
                { label: "Weight", value: weight > 0 ? `${count.format(weight)} lb` : "-" },
                { label: "Services", value: extras.length ? count.format(extras.length) : "None" },
              ]}
              foot={seesCost ? "Rates show cheapest first with carrier cost, sell price and margin." : "Rates show cheapest first. Cost and margin are hidden for your role."}
            >
              <p className="ffa-qc__for">
                For{" "}
                <strong>{target?.kind === "customer" ? target.name : target?.kind === "prospect" ? prospect.company.trim() || "a new caller" : "no one yet"}</strong>
              </p>
              <Button type="submit" variant="primary" size="lg" className="ffa-qc__go" loading={rate.isPending} disabled={!ready}>
                Get rates
              </Button>
              {!ready ? <p className="ffa-qc__missing">Fill in who it&apos;s for, both ZIP codes (city and state fill in), and every freight line.</p> : null}
            </ShipmentSoFar>
          </form>
        </>
      ) : shownResult ? (
        <div className="ffa-qc__rates" aria-live={streaming ? "off" : "polite"} aria-busy={streaming}>
          {streaming ? (
            <RateStreamProgress carriers={live?.carriers ?? []} rateCount={options.length} />
          ) : null}
          {streaming ? null : result && result.carriers.length === 0 ? (
            <div className="ffa-banner" role="note">
              <StatusPill tone="warning">No carriers connected</StatusPill>
              <span>
                No carrier has real keys and EagleLoad is off, so there are no prices. Turn on EagleLoad or add carrier keys in <Link to="/admin/carriers">Carriers &amp; API keys</Link>, then run a test.
              </span>
            </div>
          ) : allMock ? (
            <div className="ffa-banner" role="note">
              <StatusPill tone="warning">Mock rates</StatusPill>
              <span>Every carrier is in mock mode, so these are test prices, not real carrier rates.</span>
            </div>
          ) : mockCarriers.length ? (
            <div className="ffa-banner" role="note">
              <StatusPill tone="warning">Some mock</StatusPill>
              <span>Mock (test) prices from: {mockCarriers.map((c) => c.carrier).join(", ")}.</span>
            </div>
          ) : null}
          {booked && !streaming ? (
            <div className="ffa-banner ffa-banner--ok" role="status">
              <StatusPill tone="success">Booked</StatusPill>
              <span>
                {booked.carrier} BOL {booked.bolNumber ?? "pending"}. The shipment is on {shownResult.org.name}&apos;s account.
              </span>
            </div>
          ) : null}

          {picks.length > 0 && !streaming ? (
            <section className="ff-rr__picks" aria-label="Top picks">
              {picks.map((p, i) => {
                const o = p.option;
                const eta = shortDay(o.estimatedDelivery);
                return (
                  <RatePickCard
                    key={o.id}
                    id={o.id}
                    lead={i === 0}
                    tags={p.tags}
                    carrier={carrierRef(o)}
                    service={o.serviceName}
                    price={money(o.price)}
                    when={eta ? `Delivers ${eta}` : "Delivery date not given"}
                    transit={transitText(o)}
                    note={pickNote(o, p.tags)}
                    extra={staffNums(o)}
                  >
                    {canBook && !shownResult.org.prospect && !booked && !streaming ? (
                      <Button variant={i === 0 ? "primary" : "secondary"} aria-label={`Book ${o.carrierName} ${o.serviceName} for ${money(o.price)}`} onClick={() => setBooking(o)}>
                        Book
                      </Button>
                    ) : null}
                    {canOverride && o.internal && !booked && !streaming ? (
                      <Button variant="ghost" aria-label={`Override ${o.carrierName} ${o.serviceName} price`} onClick={() => setOverriding(o)}>
                        Override
                      </Button>
                    ) : null}
                  </RatePickCard>
                );
              })}
            </section>
          ) : null}

          <div className="ffa-card ffa-qc__list">
            <div className="ffa-qc__listhead">
              <div>
                <h2 className="ffa-card__title">Every rate</h2>
                <p className="ffa-muted">
                  {count.format(options.length)} rate{options.length === 1 ? "" : "s"} from {count.format(choices.length)} carrier{choices.length === 1 ? "" : "s"}
                </p>
              </div>
              <SortTabs
                label="Sort rates"
                idBase={uid}
                value={sort}
                onChange={setSort}
                items={[
                  { key: "price" as const, label: "Cheapest" },
                  { key: "transit" as const, label: "Fastest" },
                  ...(shownResult.showsCost ? [{ key: "margin" as const, label: "Best margin" }] : []),
                ]}
              />
            </div>
            {options.length > 0 ? (
              <div className="ff-rr__toolbar ffa-qc__filters">
                <RateFilterBar filters={filters} carriers={choices} shownCount={shown.length} totalCount={options.length} onChange={setFilters} />
              </div>
            ) : null}
            <div id={`${uid}-panel`} role="tabpanel" aria-labelledby={`${uid}-tab-${sort}`}>
              {options.length > 0 && sorted.length === 0 ? (
                <div className="ffa-qc__none" role="status">
                  <p>No rates match these filters.</p>
                  <Button size="sm" onClick={() => setFilters(NO_RATE_FILTERS)}>Show all rates</Button>
                </div>
              ) : null}
              <ol className="ff-rates ff-rrows" aria-label={streaming ? `Rates so far for ${shownResult.org.name}` : `Rates for ${shownResult.org.name}`} data-testid={streaming ? "live-rates" : undefined}>
                {sorted.map((o) => (
                  <RateRow
                    key={o.id}
                    best={o.id === cheapestId}
                    entering={streaming}
                    carrier={carrierRef(o)}
                    service={o.serviceName}
                    meta={<>
                      {transitText(o)}
                      {AGGREGATORS.has(o.carrier) ? <> · <span className="ffa-qc__via">via {AGGREGATOR_LABEL[o.carrier]}</span></> : null}
                    </>}
                    badges={<>
                      {o.guaranteed ? <StatusPill tone="success">Guaranteed</StatusPill> : null}
                      {o.id === cheapestId ? <StatusPill tone="info">Cheapest</StatusPill> : null}
                      {o.id === fastestId ? <StatusPill tone="info">Fastest</StatusPill> : null}
                      {o.id === marginId ? <StatusPill>Best margin</StatusPill> : null}
                      {o.override ? <StatusPill tone="warning">Price overridden</StatusPill> : null}
                    </>}
                    eta={shortDay(o.estimatedDelivery) ?? "Not given"}
                    side={<>
                      <dl className="ffa-qc__nums">
                        {shownResult.showsCost && o.internal ? (
                          <div>
                            <dt>Cost</dt>
                            <dd className="ffx-num">{money(o.internal.cost)}</dd>
                          </div>
                        ) : null}
                        <div className="ffa-qc__sell">
                          <dt>Sell</dt>
                          <dd className="ffx-num">{money(o.price)}</dd>
                        </div>
                        {shownResult.showsCost && o.internal ? (
                          <div title={o.internal.markupRationale ?? undefined}>
                            <dt>Margin</dt>
                            <dd className="ffx-num">
                              {money(o.internal.margin)}
                              <span className="ffa-qc__pct">{o.price.cents > 0 ? pct.format(o.internal.margin.cents / o.price.cents) : ""}</span>
                            </dd>
                          </div>
                        ) : null}
                      </dl>
                      {!booked && !streaming && (canOverride || canBook) ? (
                        <div className="ff-rrow__btns ffa-qc__rowbtns">
                          {canOverride && o.internal ? (
                            <Button size="sm" variant="ghost" onClick={() => setOverriding(o)}>
                              Override price
                            </Button>
                          ) : null}
                          {canBook && !shownResult.org.prospect ? (
                            <Button size="sm" className="ffa-qc__book" onClick={() => setBooking(o)}>
                              Book this
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </>}
                  >
                    {o.override ? (
                      <p className="ff-rrow__why ffa-qc__override">
                        Price overridden: {o.override.reason}
                        {o.override.originalCents ? ` (was ${usd.format(o.override.originalCents / 100)})` : ""}
                      </p>
                    ) : null}
                  </RateRow>
                ))}
              </ol>
            </div>
          </div>
          {shownResult.org.prospect && !streaming ? (
            <p className="ffa-muted">Prospects can be quoted but not booked: convert them to a customer with credit or payment set up first.</p>
          ) : null}
          {shownResult.quote.errors.length ? (
            <p className="ffa-muted">No answer from: {shownResult.quote.errors.map((e) => e.carrier).join(", ")}.</p>
          ) : null}
          <p className="ffa-qc__foot">Follow-ups, outcomes and re-quotes for every quote live in <Link to="/quotes">Sales &gt; Quotes</Link>.</p>
        </div>
      ) : null}
      {overriding && result ? (
        <OverrideDialog
          quoteId={result.quote.id}
          option={overriding}
          onClose={() => setOverriding(null)}
          onDone={(opts) => setResult({ ...result, quote: { ...result.quote, options: opts } })}
        />
      ) : null}
      {booking && result ? (
        <BookDialog
          quoteId={result.quote.id}
          option={booking}
          originStop={origin}
          destinationStop={destination}
          onClose={() => setBooking(null)}
          onDone={(s) => {
            setBooked({ id: s.id, bolNumber: s.bolNumber, carrier: booking.carrierName });
            setBooking(null);
          }}
        />
      ) : null}
    </section>
  );
}

function OverrideDialog({ quoteId, option, onClose, onDone }: { quoteId: string; option: ConsoleQuoteOption; onClose: () => void; onDone: (options: ConsoleQuoteOption[]) => void }) {
  const [price, setPrice] = useState((option.price.cents / 100).toFixed(2));
  const [reason, setReason] = useState("");
  const cents = Math.round(n(price) * 100);
  const cost = option.internal?.cost.cents ?? 0;
  const valid = Number.isFinite(cents) && cents >= cost && reason.trim().length >= 5;
  const save = useMutation({
    mutationFn: () => api<{ quote: { options: ConsoleQuoteOption[] } }>(`/v1/console/quotes/${quoteId}/options/${option.id}/override`, { method: "POST", body: { priceCents: cents, reason: reason.trim() } }),
    onSuccess: (r) => {
      onDone(r.quote.options);
      notify.success("Price updated", `${option.carrierName}: ${usd.format(cents / 100)}`);
      onClose();
    },
    onError: (e) => notify.error("Couldn't change the price", message(e)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Override ${option.carrierName} price`}
      description={`Carrier cost ${usd.format(cost / 100)}. The change and your reason are logged.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Save price
          </Button>
        </>
      }
    >
      <div className="ffa-form">
        <TextField label="Sell price" prefix="$" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} error={cents < cost ? "Can't be below the carrier cost" : undefined} hint={cents >= cost ? `Margin ${usd.format((cents - cost) / 100)}${cents > 0 ? ` (${pct.format((cents - cost) / cents)})` : ""}` : undefined} />
        <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} hint="For example: matching a competitor's quote" />
      </div>
    </Dialog>
  );
}

type BookEnd = { companyName: string; line1: string; line2: string; city: string; region: string; contactName: string; phone: string };
const endFrom = (s: Stop): BookEnd => ({ companyName: s.companyName, line1: s.line1, line2: "", city: s.city, region: s.region.toUpperCase(), contactName: "", phone: "" });

/** Book on the customer's behalf: the full pickup and delivery address is required, prefilled from the quote. */
function BookDialog({ quoteId, option, originStop, destinationStop, onClose, onDone }: { quoteId: string; option: ConsoleQuoteOption; originStop: Stop; destinationStop: Stop; onClose: () => void; onDone: (s: { id: string; bolNumber: string | null }) => void }) {
  const [o, setO] = useState<BookEnd>(() => endFrom(originStop));
  const [d, setD] = useState<BookEnd>(() => endFrom(destinationStop));
  const [pickup, setPickup] = useState(true);
  const [tried, setTried] = useState(false);
  const errors = { ...bookingAddressErrors("origin", o, "pickup"), ...bookingAddressErrors("destination", d, "delivery") };
  const valid = Object.keys(errors).length === 0;
  const body = (x: BookEnd) =>
    Object.fromEntries(
      Object.entries({ street: x.line1, street2: x.line2, city: x.city, region: x.region.toUpperCase(), company: x.companyName, contactName: x.contactName, phone: x.phone })
        .map(([k, v]) => [k, v.trim()])
        .filter(([, v]) => v !== ""),
    );
  const book = useMutation({
    mutationFn: () => api<{ shipment: { id: string; bolNumber: string | null } }>(`/v1/console/quotes/${quoteId}/book`, { method: "POST", body: { optionId: option.id, origin: body(o), destination: body(d), schedulePickup: pickup } }),
    onSuccess: (r) => {
      onDone(r.shipment);
      notify.success("Booked", `BOL ${r.shipment.bolNumber ?? "pending"}`);
    },
    onError: (e) => notify.error("Booking didn't go through", message(e)),
  });
  const shown = tried ? errors : {};
  const end = (prefix: "origin" | "destination", legend: string, v: BookEnd, set: (v: BookEnd) => void, stop: Stop) => (
    <>
      <BookingAddressFields
        prefix={prefix}
        legend={legend}
        value={v}
        onChange={(a) => set({ ...v, ...a })}
        errors={shown}
        postalCode={stop.postalCode}
        country={stop.country}
        source={addressSource}
      >
        <div className="ffa-grid2">
          <TextField label={prefix === "origin" ? "Pickup contact" : "Delivery contact"} optional autoComplete="name" value={v.contactName} onChange={(e) => set({ ...v, contactName: e.target.value })} />
          <TextField label={prefix === "origin" ? "Pickup phone" : "Delivery phone"} optional type="tel" autoComplete="tel" value={v.phone} onChange={(e) => set({ ...v, phone: e.target.value })} />
        </div>
      </BookingAddressFields>
    </>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Book ${option.carrierName}`}
      description={`${option.serviceName} at ${money(option.price)}. Books on the customer's account and creates the BOL.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={book.isPending}
            onClick={() => {
              setTried(true);
              if (valid) book.mutate();
            }}
          >
            Book shipment
          </Button>
        </>
      }
    >
      <div className="ffa-form">
        {end("origin", "Pickup", o, setO, originStop)}
        {end("destination", "Delivery", d, setD, destinationStop)}
        <Toggle label="Schedule the pickup with the carrier" checked={pickup} onChange={setPickup} />
      </div>
    </Dialog>
  );
}
